import Icon from "core/icon/Icon";
import "./ImportTool.scss";
import { Typography } from "core/typography/Typography";
import { FormattedMessage, useIntl } from "react-intl";
import DownloadIcon from "assets/icons/download-2.svg";
import ExcelIcon from "assets/icons/excel.svg";
import { useState, useRef } from "react";
import Button from "core/buttons/button/Button";
import { uploadFileHelper } from "utils/helper";
import { tabletSize } from "utils/screenSizes";
import {
  QUERY_STATUS,
  ACCEPTED_TYPES,
  POPUP_STATUS,
  LANG,
  API_ERRORS,
} from "constants/constants";
import {
  ERROR_INVALID_EXCEL_FILE,
  ERROR_INVALID_SIZE_EXCEL_FILE,
  SUCCESS_IMPORTED_FILE,
  SUCCESS_IMPORTED_FILE_WITH_ERRORS,
} from "constants/popup-codes";
import PropTypes from "prop-types";
import { useUserContext } from "api/user/UserProvider";
import ValidationModal from "components/modal/ValidationModal";
import colors from "assets/styles/mixins/colors.scss";

const ImportTool = ({
  query,
  setQueryStatus,
  queryStatus,
  progression,
  setProgression,
  setCode,
  setStatus,
  serverResponse,
  setServerResponse,
  fileSizeLimit,
}) => {
  const { success_count, failure_count, errors, total } = serverResponse || {};
  const intl = useIntl();
  const { lang } = useUserContext();
  const [isOpen, setIsOpen] = useState(false);
  const [fileObj, setFileObj] = useState({});
  const { name, type, size } = fileObj || {};
  const inputFileRef = useRef();
  const fileSizeFormatter = (size) =>
    `${(size / 1000).toFixed()}${lang === LANG.FR ? "Ko" : "Kb"}`;

  const initialize = () => {
    setQueryStatus(QUERY_STATUS.INITIAL);
    setFileObj({});
    inputFileRef.current.value = "";
    setProgression(0);
    setServerResponse();
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    initialize();
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      setFileObj(file);
    }
  };

  const buttonsHandler = ({ action }) => {
    if (action === "cancel") {
      initialize();
      return;
    }
    if (action === "import" && name) {
      const isInvalidType = !ACCEPTED_TYPES.includes(type);
      const isInvalidSize = size > fileSizeLimit.value;
      if (isInvalidType || isInvalidSize) {
        setStatus(POPUP_STATUS.ERROR);
        setCode(
          isInvalidType
            ? ERROR_INVALID_EXCEL_FILE
            : ERROR_INVALID_SIZE_EXCEL_FILE
        );
      } else {
        uploadFileHelper({
          file: fileObj,
          type: "xls-file",
          uploadQueryFunc: query,
        });
        setIsOpen(true);
      }
    }
  };

  const handleFileClick = () => {
    initialize();
    document.getElementById("fileInput").click();
  };

  const translatedError = (error) => {
    const apiError = API_ERRORS?.find((e) => e?.code === error)?.errorMessage;
    return (
      apiError &&
      `${intl.formatMessage({
        id: apiError?.messageId,
        defaultMessage: apiError?.defaultMessage,
      })}`
    );
  };

  const formattedError = (error) =>
    typeof error === "string"
      ? error
      : `${lang === LANG.FR ? "Ligne" : "Line"} ${
          error?.line + " :"
        } ${translatedError(error?.error)}`;

  const renderErrorsDetails = () =>
    name &&
    failure_count > 0 && (
      <div className="errors-details">
        <div className="text-1 violet" style={{ marginBottom: "0.5rem" }}>
          <FormattedMessage
            id="error_lines_details"
            defaultMessage="Détail des erreurs :"
          />
        </div>
        <div className="server-errors">
          {errors?.map((error, index) => (
            <span key={index}>{formattedError(error)}</span>
          ))}
        </div>
      </div>
    );
  return (
    <>
      <ValidationModal
        open={isOpen}
        status={queryStatus}
        onClose={() => {
          setIsOpen(false);
          setQueryStatus(QUERY_STATUS.INITIAL);
        }}
        messages={{
          pendingTitleId: "import_title",
          pendingTitleDefault: "Importation de donnée",
          pendingMessageId: "import_loading",
          pendingMessageDefault: "L'importation de votre fichier est en cours",
          successTitleId: "import_title",
          successTitleDefault: "Importation de donnée",
          successMessageId:
            errors?.length > 0
              ? SUCCESS_IMPORTED_FILE_WITH_ERRORS
              : SUCCESS_IMPORTED_FILE,
          successMessageDefault: `Votre fichier a été importé avec succès${
            errors?.length > 0 ? " mais comporte des erreurs" : ""
          }.`,
        }}
      />
      <div className="section-1">
        <div>
          <Typography
            className="title-3 violet text-uppercase"
            type="h2"
            label={
              <FormattedMessage id="excel_file" defaultMessage="Ficher Excel" />
            }
          />
        </div>
        <div className="section-content-wrapper">
          <div className="upload-wrapper">
            <section
              aria-label="upload-zone"
              className="download-input"
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <input
                type="file"
                id="fileInput"
                accept=".xlsx, .xls"
                style={{ display: "none" }}
                ref={inputFileRef}
                onChange={(event) => {
                  setFileObj(event?.target?.files[0]);
                }}
              />
              <button style={{ cursor: "pointer" }} onClick={handleFileClick}>
                <img
                  src={DownloadIcon}
                  alt="Download file icon"
                  className="download-2"
                />
              </button>
              <Typography
                className="text-1 violet"
                type="p"
                label={
                  <FormattedMessage
                    id="choose_a_file"
                    defaultMessage="Choisis un fichier"
                  />
                }
              />
              <Typography
                className="text-3 gray"
                type="p"
                label={
                  <>
                    <FormattedMessage
                      id="xlsx_format"
                      defaultMessage="Format XLSX"
                    />
                    {` - ${
                      lang === LANG.FR ? fileSizeLimit?.fr : fileSizeLimit?.en
                    } max.`}
                  </>
                }
              />
            </section>
            {name && (
              <div className="download-progress">
                <div className="wrapper-1">
                  <div className="subwrapper-1">
                    <img
                      src={ExcelIcon}
                      alt="Excel icon"
                      className="excel-icon"
                    />
                    <div>
                      <div className="file-name text-1">{name}</div>
                      <div className="file-size text-3">
                        {fileSizeFormatter(size)}
                      </div>
                    </div>
                  </div>
                  <Icon
                    id="close-icon"
                    color="violet"
                    icon="close"
                    onClick={() => buttonsHandler({ action: "cancel" })}
                  />
                </div>
                <div className="progression-wrapper">
                  <div className="progression-bar">
                    <div
                      className="progression-bar-fill"
                      style={{ width: `${progression}%` }}
                    ></div>
                  </div>
                  <div className="file-size text-3 progression-percentage">
                    {`${progression}%`}
                  </div>
                </div>
                {total > 0 && (
                  <div className="server-response-infos-wrapper">
                    <div className="success-lines-nb">
                      <FormattedMessage
                        id="success_lines_imported"
                        defaultMessage="Nombre de lignes importées avec succès :"
                      />{" "}
                      {success_count}
                    </div>
                    {failure_count > 0 && (
                      <div className="error-lines-nb">
                        <FormattedMessage
                          id="error_lines_imported"
                          defaultMessage="Nombre de lignes importées avec des erreurs :"
                        />{" "}
                        {failure_count}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            {tabletSize && renderErrorsDetails()}
            <div className="form-buttons">
              <Button
                className="btn-medium btn-outline"
                onClick={() => {
                  buttonsHandler({ action: "cancel" });
                }}
                id="button-cancel"
              >
                <FormattedMessage id="cancel" defaultMessage="Annuler" />
              </Button>

              <Button
                color="btn-medium gray-700"
                id="button-import"
                onClick={() => buttonsHandler({ action: "import" })}
                disabled={queryStatus === QUERY_STATUS.LOADING}
                backgroundColor={
                  queryStatus === QUERY_STATUS.LOADING && colors.disabledGrey
                }
              >
                <FormattedMessage id="import" defaultMessage="Importer" />
              </Button>
            </div>
          </div>
          {!tabletSize && renderErrorsDetails()}
        </div>
      </div>
    </>
  );
};

export default ImportTool;

ImportTool.propTypes = {
  query: PropTypes.func,
  setQueryStatus: PropTypes.func,
  progression: PropTypes.number,
  setProgression: PropTypes.func,
  setCode: PropTypes.func,
  setStatus: PropTypes.func,
  serverResponse: PropTypes.object,
  setServerResponse: PropTypes.func,
  fileSizeLimit: PropTypes.object,
};
